import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Card,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { getAllDemoRequests, deleteDemoRequest } from "../../../Services/BrokersApi";
import { themeColors } from "../../../theme/colors";

export default function AllRejectedDemoTap() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // new state to track which item is being deleted
  const [deletingId, setDeletingId] = useState(null);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const res = await getAllDemoRequests(null, token);
      const all = res?.data?.data ?? res?.data ?? [];
      const rejected = Array.isArray(all) ? all.filter((r) => r.status === "rejected") : [];
      setItems(rejected);
      setPage(0);
    } catch (err) {
      setError(err?.message || "خطأ أثناء جلب الطلبات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // new delete handler
  const handleDelete = async (id) => {
    const ok = window.confirm("هل أنت متأكد من حذف الطلب نهائياً؟");
    if (!ok) return;
    setDeletingId(id);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      await deleteDemoRequest(id, token);
      // remove deleted item from list
      setItems((prev) => prev.filter((r) => r.id !== id));
      // adjust page if necessary
      const newCount = Math.max(0, items.length - 1);
      const maxPage = Math.max(0, Math.ceil(newCount / rowsPerPage) - 1);
      if (page > maxPage) setPage(maxPage);
    } catch (err) {
      setError(err?.message || "خطأ أثناء حذف الطلب");
    } finally {
      setDeletingId(null);
    }
  };

  
  const pagedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        p={2}
        sx={{
          bgcolor: `${themeColors.error}08`,
          borderRadius: 2,
          border: `1px solid ${themeColors.error}30`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <CancelIcon sx={{ color: themeColors.error }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: themeColors.text.primary }}>
            الطلبات المرفوضة
          </Typography>
          <Chip
            label={items.length}
            size="small"
            sx={{
              bgcolor: themeColors.error,
              color: "white",
              fontWeight: 600,
            }}
          />
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={load}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{
            borderRadius: 2,
            borderColor: themeColors.error,
            color: themeColors.error,
            "&:hover": {
              borderColor: "#D32F2F",
              bgcolor: `${themeColors.error}08`,
            },
          }}
        >
          {loading ? "جاري التحميل..." : "تحديث"}
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} sx={{ color: themeColors.error }} />
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
          }}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && items.length === 0 && (
        <Card
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 3,
            border: `2px dashed ${themeColors.border.main}`,
          }}
        >
          <CancelIcon sx={{ fontSize: 80, color: themeColors.text.light, mb: 2 }} />
          <Typography variant="h6" sx={{ color: themeColors.text.secondary, mb: 1 }}>
            لا توجد طلبات مرفوضة
          </Typography>
          <Typography variant="body2" sx={{ color: themeColors.text.secondary }}>
            جميع الطلبات المرفوضة ستظهر هنا
          </Typography>
        </Card>
      )}

      {!loading && items.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${themeColors.border.light}`,
          }}
        >
          <TableContainer>
            <Table sx={{ direction: "rtl" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: themeColors.background.default,
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      borderBottom: `2px solid ${themeColors.error}`,
                    }}
                  >
                    العميل
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: themeColors.background.default,
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      borderBottom: `2px solid ${themeColors.error}`,
                    }}
                  >
                    اسم النسخة
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: themeColors.background.default,
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      borderBottom: `2px solid ${themeColors.error}`,
                    }}
                  >
                    الباقة
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: themeColors.background.default,
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      borderBottom: `2px solid ${themeColors.error}`,
                    }}
                  >
                    الملاحظة
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: themeColors.background.default,
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      borderBottom: `2px solid ${themeColors.error}`,
                    }}
                  >
                    الحالة
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      bgcolor: themeColors.background.default,
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      borderBottom: `2px solid ${themeColors.error}`,
                    }}
                  >
                    تاريخ الإنشاء
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      bgcolor: themeColors.background.default,
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      borderBottom: `2px solid ${themeColors.error}`,
                    }}
                  >
                    الإجراءات
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedItems.map((r) => (
                  <TableRow
                    key={r.id}
                    sx={{
                      "&:hover": { bgcolor: themeColors.background.hover },
                      "&:nth-of-type(even)": { bgcolor: "rgba(0,0,0,0.02)" },
                    }}
                  >
                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: themeColors.text.primary }}>
                          {r.user?.name ?? "-"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                          {r.user?.email ?? "-"}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: themeColors.text.secondary }}>
                          📞 {r.user?.phone ?? "-"}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: themeColors.text.secondary }}>
                          🕐 {formatDate(r.user?.created_at)}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.tenant_name ?? "-"}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.plane?.plane_name ?? "-"}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: themeColors.text.secondary }}>
                          شهري: {r.plane?.monthly_price ?? "-"}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: themeColors.text.secondary }}>
                          سنوي: {r.plane?.yearly_price ?? "-"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                        {r.notes ?? "-"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={r.status ?? "-"}
                        size="small"
                        icon={<CancelIcon />}
                        sx={{
                          bgcolor: `${themeColors.error}15`,
                          color: themeColors.error,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
                        {formatDate(r.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="حذف نهائي">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(r.id)}
                          disabled={loading || deletingId === r.id}
                          sx={{
                            color: themeColors.error,
                            "&:hover": {
                              bgcolor: `${themeColors.error}15`,
                            },
                          }}
                        >
                          {deletingId === r.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={items.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="عدد الصفوف:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
            sx={{
              borderTop: `1px solid ${themeColors.border.light}`,
              bgcolor: themeColors.background.default,
            }}
          />
        </Paper>
      )}
    </Box>
  );
}